/**
 * Main Screen - Mining tab with action bar + generator list
 * Skeleton build + patch rendering pattern
 */
import { GENERATORS, getMilestoneMultiplier } from '../game/config/generators.js';
import { getCurrentCPS } from '../game/state.js';
import { formatNumber, getGeneratorCost } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { onRender, getState, dispatch, setInnerHTML, requestRender, markScreenDirty, showToast, showModal } from './renderer.js';
import { createMarketPanel, updateMarketPanel } from './marketPanel.js';
import { AdManager, getAdRewards } from '../monetization/AdManager.js';

// Generator ID -> image file mapping
const GEN_IMAGES: Record<string, string> = {
  gen1: '/images/gen-smartphone.png',
  gen2: '/images/gen-laptop.png',
  gen3: '/images/gen-desktop.png',
  gen4: '/images/gen-gpu.png',
  gen5: '/images/gen-asic.png',
  gen6: '/images/gen-farm.png',
  gen7: '/images/gen-datacenter.png',
  gen8: '/images/gen-quantum.png',
  gen9: '/images/gen-ai-bot.png',
  gen10: '/images/gen-satellite.png',
  gen11: '/images/gen-moon-base.png',
  gen12: '/images/gen-galaxy.png',
};

let actionBar: HTMLDivElement | null = null;
let adBar: HTMLDivElement | null = null;
let genList: HTMLDivElement | null = null;
let lastPurchasedGen: string | null = null;

export function initMainScreen() {
  const content = document.getElementById('screen-content');
  if (!content) return;

  // Skeleton build (once)
  const wrapper = document.createElement('div');
  wrapper.id = 'main-screen';
  wrapper.className = 'screen-panel';

  // Market panel (news + chart + manipulation)
  const market = createMarketPanel();
  wrapper.appendChild(market);

  actionBar = document.createElement('div');
  actionBar.id = 'action-bar';
  actionBar.className = 'action-bar';
  wrapper.appendChild(actionBar);

  adBar = document.createElement('div');
  adBar.id = 'ad-bar';
  adBar.className = 'ad-bar';
  wrapper.appendChild(adBar);

  genList = document.createElement('div');
  genList.id = 'gen-list';
  genList.className = 'gen-list';
  wrapper.appendChild(genList);

  content.appendChild(wrapper);

  // Register render callback - always render on main tab
  onRender(renderMain, { alwaysTabs: ['main'] });
}

function renderMain() {
  const st = getState();
  if (!actionBar || !genList) return;

  // Market panel (news + chart + manipulation buttons)
  updateMarketPanel();

  // Action bar
  renderActionBar(st);

  // Ad reward bar
  renderAdBar(st);

  // Generator list
  renderGenList(st);
}

function renderActionBar(st: ReturnType<typeof getState>) {
  if (!actionBar) return;
  const now = Date.now();
  let html = '';

  if (st.dailyBonus.canClaim) {
    html += `<button class="act-btn act-btn-gold" data-action="daily">${t('action_daily')}</button>`;
  }

  const boostReady = !st.clickBooster.active && now >= st.clickBooster.cooldownUntil;
  if (boostReady) {
    html += `<button class="act-btn act-btn-purple" data-action="turbo">${t('action_turbo')}</button>`;
  } else if (st.clickBooster.active) {
    const rem = Math.ceil((st.clickBooster.expiresAt - now) / 1000);
    html += `<button class="act-btn act-btn-purple-dim" disabled>${t('action_turbo')} ${rem}s</button>`;
  }

  if (setInnerHTML(actionBar, html)) {
    actionBar.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const action = (e.currentTarget as HTMLElement).dataset.action;
        if (action === 'daily') {
          dispatch({ type: 'CLAIM_DAILY_BONUS' });
          playSfx('achievement');
          markScreenDirty();
          requestRender();
        } else if (action === 'turbo') {
          dispatch({ type: 'ACTIVATE_CLICK_BOOSTER' });
          playSfx('boost');
          markScreenDirty();
          requestRender();
        }
      });
    });
  }
}

function renderAdBar(st: ReturnType<typeof getState>) {
  if (!adBar) return;
  const now = Date.now();
  let html = '';

  // x10 Income Boost
  const adBoostActive = st.activeGoldenEffects.some(e => e.type === 'ad_income_x5' && now < e.expiresAt);
  const adBoostCooldown = now < st.adBoostCooldownUntil;
  if (adBoostActive) {
    const fx = st.activeGoldenEffects.find(e => e.type === 'ad_income_x5' && now < e.expiresAt);
    const rem = fx ? Math.ceil((fx.expiresAt - now) / 1000) : 0;
    html += `<button class="ad-btn" disabled>x10 ${rem}s</button>`;
  } else if (adBoostCooldown) {
    const rem = Math.ceil((st.adBoostCooldownUntil - now) / 1000);
    html += `<button class="ad-btn" disabled>x10 ${rem}s</button>`;
  } else {
    html += `<button class="ad-btn" data-ad="income_x10">x10 <span class="ad-reward">${t('ad_income_x5_desc')}</span></button>`;
  }

  // 2h Timeskip
  html += `<button class="ad-btn" data-ad="timeskip">+2h <span class="ad-reward">${t('ad_timeskip_desc')}</span></button>`;

  // Tap x10
  const tapBoostActive = st.activeGoldenEffects.some(e => e.type === 'ad_tap_x10' && now < e.expiresAt);
  if (tapBoostActive) {
    const fx = st.activeGoldenEffects.find(e => e.type === 'ad_tap_x10' && now < e.expiresAt);
    const rem = fx ? Math.ceil((fx.expiresAt - now) / 1000) : 0;
    html += `<button class="ad-btn" disabled>TAP x10 ${rem}s</button>`;
  } else {
    html += `<button class="ad-btn" data-ad="tap_x10">TAP x10 <span class="ad-reward">${t('ad_tap_x10_desc')}</span></button>`;
  }

  if (setInnerHTML(adBar, html)) {
    adBar.querySelectorAll('[data-ad]').forEach(btn => {
      btn.addEventListener('click', () => {
        const adType = (btn as HTMLElement).dataset.ad!;
        showAdConfirmPopup(adType);
      });
    });
  }
}

function showAdConfirmPopup(adType: string) {
  const rewards = getAdRewards();
  let title = '';
  let desc = '';
  let onConfirm: () => void = () => {};

  if (adType === 'income_x10') {
    title = t('ad_popup_income_title');
    desc = t('ad_popup_income_desc');
    onConfirm = async () => {
      const result = await AdManager.showRewardedAd(rewards.INCOME_X5);
      if (result) {
        dispatch({ type: 'AD_BOOST_X5' });
        playSfx('boost');
        showToast(t('ad_income_x5'));
      }
      markScreenDirty();
      requestRender();
    };
  } else if (adType === 'timeskip') {
    const st = getState();
    const cps = getCurrentCPS(st);
    const reward = Math.floor(cps * 7200);
    title = t('ad_popup_timeskip_title');
    desc = `${t('ad_popup_timeskip_desc')}<br><br><span style="color:var(--gold);font-size:1.1em;font-weight:700">+${formatNumber(reward)}</span>`;
    onConfirm = async () => {
      const result = await AdManager.showRewardedAd(rewards.TIMESKIP);
      if (result) {
        dispatch({ type: 'TIMESKIP', payload: { seconds: 7200 } });
        playSfx('boost');
        showToast(t('ad_timeskip'));
      }
      markScreenDirty();
      requestRender();
    };
  } else if (adType === 'tap_x10') {
    title = t('ad_popup_tap_title');
    desc = t('ad_popup_tap_desc');
    onConfirm = async () => {
      const result = await AdManager.showRewardedAd(rewards.TAP_X10);
      if (result) {
        dispatch({ type: 'TAP_BOOST_X10' });
        playSfx('boost');
        showToast(t('ad_tap_x10'));
      }
      markScreenDirty();
      requestRender();
    };
  }

  showModal(
    title,
    `<div class="modal-detail">${desc}</div>`,
    [
      { label: t('ad_popup_watch'), color: 'var(--gold)', onClick: onConfirm },
      { label: t('ad_popup_cancel'), color: 'rgba(255,255,255,0.1)', onClick: () => {} },
    ]
  );
}

function renderGenList(st: ReturnType<typeof getState>) {
  if (!genList) return;

  let html = '';
  for (let i = 0; i < GENERATORS.length; i++) {
    const gc = GENERATORS[i];
    const gen = st.generators[gc.id];
    if (!gen) continue;

    const cost = getGeneratorCost(gc.baseCost, gen.count, gc.costMultiplier);
    const afford = st.coins >= cost;
    const unlocked = i === 0 || (st.generators[GENERATORS[i - 1].id]?.count || 0) > 0;
    const mm = getMilestoneMultiplier(gen.count);
    const prod = gc.baseProduction * gen.count * mm;
    const imgSrc = GEN_IMAGES[gc.id] || '';
    const name = t('gen_' + gc.id) || gc.name;

    if (!unlocked) {
      html += `<div class="gen-card gen-locked">
        <div class="gen-icon"><img src="${imgSrc}" alt="${name}" loading="lazy" /></div>
        <div class="gen-info">
          <div class="gen-name">${name}</div>
          <div class="gen-locked-text">${t('gen_locked')}</div>
        </div>
      </div>`;
    } else {
      // Milestone progress
      let milestoneHtml = '';
      if (gen.count > 0) {
        const milestones = [25, 50, 75, 100, 125, 150, 175, 200];
        const next = milestones.find(m => m > gen.count);
        if (next) {
          const prev = milestones.filter(m => m <= gen.count).pop() || 0;
          const pct = ((gen.count - prev) / (next - prev)) * 100;
          milestoneHtml = `<div class="gen-milestone"><div class="gen-milestone-fill" style="width:${pct}%"></div></div>`;
        }
      }

      html += `<div class="gen-card${afford ? ' afford' : ''}" data-gen="${gc.id}">
        <div class="gen-icon"><img src="${imgSrc}" alt="${name}" loading="lazy" /></div>
        <div class="gen-info">
          <div class="gen-name">${name} <span class="gen-count">x${gen.count}</span></div>
          <div class="gen-prod">+${formatNumber(prod)}${t('hud_cps')}</div>
        </div>
        <div class="gen-right">
          <div class="gen-cost${afford ? '' : ' gen-cost-red'}">${formatNumber(cost)}</div>
          ${milestoneHtml}
        </div>
      </div>`;
    }
  }

  if (setInnerHTML(genList, html)) {
    // Apply purchase flash to newly rendered card
    if (lastPurchasedGen) {
      const flashCard = genList.querySelector(`[data-gen="${lastPurchasedGen}"]`) as HTMLElement | null;
      if (flashCard) {
        flashCard.classList.add('gen-flash');
        setTimeout(() => flashCard.classList.remove('gen-flash'), 500);
      }
      lastPurchasedGen = null;
    }
    genList.querySelectorAll('[data-gen]').forEach(card => {
      card.addEventListener('click', () => {
        const genId = (card as HTMLElement).dataset.gen!;
        const currentSt = getState();
        const gc = GENERATORS.find(g => g.id === genId);
        if (!gc) return;
        const gen = currentSt.generators[genId];
        if (!gen) return;
        const c = getGeneratorCost(gc.baseCost, gen.count, gc.costMultiplier);
        if (currentSt.coins >= c) {
          lastPurchasedGen = genId;
          dispatch({ type: 'BUY_GENERATOR', payload: { generatorId: genId } });
          playSfx('purchase');
          markScreenDirty();
          requestRender();
        } else {
          playSfx('error');
        }
      });
    });
  }
}
